import { config } from '@/app/config';
import { getSupabase } from '@/supabase/client';

const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

export function resolveAvatarUrl(
  avatarPath: string | null | undefined,
  cacheKey?: string | null,
): string | null {
  if (!avatarPath) return null;
  if (
    avatarPath.startsWith('data:') ||
    avatarPath.startsWith('blob:') ||
    avatarPath.startsWith('http://') ||
    avatarPath.startsWith('https://')
  ) {
    return avatarPath;
  }
  if (config.mode !== 'production') return null;
  const supabase = getSupabase();
  if (!supabase) return null;
  const { data } = supabase.storage.from('avatars').getPublicUrl(avatarPath);
  const url = data.publicUrl;
  if (!url) return null;
  if (cacheKey) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}v=${encodeURIComponent(cacheKey)}`;
  }
  return url;
}

function extensionForMime(mime: string): string {
  if (mime === 'image/webp') return 'webp';
  if (mime === 'image/png') return 'png';
  return 'jpg';
}

/** Upload avatar to shared Storage; returns storage path for profiles.avatar_path. */
export async function uploadUserAvatar(userId: string, file: File): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('unsupported_image');
  }
  if (file.size > MAX_AVATAR_BYTES) {
    throw new Error('too_large');
  }

  if (config.mode !== 'production') {
    // Demo: persist as data URL in avatar_path (local Dexie only).
    const dataUrl = await readAsDataUrl(file);
    return dataUrl;
  }

  const supabase = getSupabase();
  if (!supabase) throw new Error('no_supabase');

  const ext = extensionForMime(file.type);
  const path = `${userId}/avatar.${ext}`;
  const { error } = await supabase.storage.from('avatars').upload(path, file, {
    upsert: true,
    contentType: file.type,
    cacheControl: '3600',
  });
  if (error) throw error;
  return path;
}

export async function removeUserAvatar(userId: string, avatarPath: string | null): Promise<void> {
  if (!avatarPath || config.mode !== 'production') return;
  if (avatarPath.startsWith('data:') || avatarPath.startsWith('http')) return;
  const supabase = getSupabase();
  if (!supabase) return;
  await supabase.storage.from('avatars').remove([avatarPath]);
  // Also try common sibling extensions left by previous uploads
  const base = `${userId}/avatar`;
  await supabase.storage.from('avatars').remove([`${base}.jpg`, `${base}.jpeg`, `${base}.png`, `${base}.webp`]);
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('read_failed'));
    reader.readAsDataURL(file);
  });
}
